import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

const STORAGE_KEY = 'app_version_seen';

const AppVersionChecker = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // آخر نسخة “مُشاهَدة” على هذا الجهاز (حتى لو فتحت تبويب جديد)
  const seenVersionRef = useRef<string | null>(null);
  // آخر نسخة حالية على السيرفر (نستخدمها عند الضغط على “تحديث الآن” لتثبيت النسخة بالمخزن)
  const latestServerVersionRef = useRef<string | null>(null);
  const isSubscribedRef = useRef(false);

  const handleServerVersion = (serverVersion: string | null | undefined, source: string) => {
    if (!serverVersion) return;
    latestServerVersionRef.current = serverVersion;

    const seen = seenVersionRef.current;
    if (seen && serverVersion !== seen) {
      console.log(`Update detected via ${source}:`, serverVersion, 'vs seen', seen);
      setShowUpdateBanner(true);
      return;
    }

    // أول مرة فقط: خزّن النسخة الحالية كنسخة “مُشاهَدة”
    if (!seen) {
      seenVersionRef.current = serverVersion;
      try {
        localStorage.setItem(STORAGE_KEY, serverVersion);
      } catch {
        // ignore
      }
      console.log('Initial seen app version:', serverVersion);
    }
  };

  useEffect(() => {
    if (isSubscribedRef.current) return;
    isSubscribedRef.current = true;

    // اقرأ النسخة المحفوظة (هذا اللي يخلي التبويب الجديد يعرف انه “قديمة”)
    try {
      seenVersionRef.current = localStorage.getItem(STORAGE_KEY);
    } catch {
      seenVersionRef.current = null;
    }

    const checkForUpdates = async () => {
      try {
        const { data } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'app_version')
          .maybeSingle();

        handleServerVersion(data?.value, 'polling');
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    };

    // Initial check
    checkForUpdates();

    const channel = supabase
      .channel('app-version-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'settings',
          filter: 'key=eq.app_version',
        },
        (payload) => {
          console.log('App version changed via realtime:', payload);
          const newValue = (payload.new as { value?: string })?.value;
          handleServerVersion(newValue, 'realtime');
        }
      )
      .subscribe();

    const interval = setInterval(checkForUpdates, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
      isSubscribedRef.current = false;
    };
  }, []);

  const handleRefresh = () => {
    // نثبت النسخة الأخيرة كمشاهَدة حتى ما يرجع يطلع البانر بعد الريلود
    try {
      if (latestServerVersionRef.current) {
        localStorage.setItem(STORAGE_KEY, latestServerVersionRef.current);
      }
    } catch {
      // ignore
    }

    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    window.location.reload();
  };

  if (!showUpdateBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-primary text-primary-foreground p-3 shadow-lg animate-in slide-in-from-top duration-300">
      <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
        <p className="text-sm font-medium">🆕 تحديث جديد متوفر!</p>
        <Button size="sm" variant="secondary" onClick={handleRefresh} className="shrink-0">
          <RefreshCw className="w-4 h-4 ml-1" />
          تحديث الآن
        </Button>
      </div>
    </div>
  );
};

export default AppVersionChecker;